// The approved Booking State Machine, as the database derives it (0227).
//
// `booking_status` stays the detailed operational status; these are the spec's
// states (`06 State Models/Booking State Machine`), computed in SQL by
// `v_booking_entry_state_base` and read through `v_client_booking_entries` /
// `v_ops_booking_entries`. Nothing here re-derives them — a second derivation
// in TypeScript is how the card and the fee (0228) would start disagreeing.
// This module only names the values and refuses anything it does not know.
//
// Two levels (plan Q11):
//   · ENTRY — one Therapist in one booking: what a Therapist card shows
//     (SCHED-028, THER-041). Carries the rider/reassignment states.
//   · BOOKING — the group, coarse only. The spec gives no precedence for
//     folding two entries' rider states into one, so none is invented.

export const BOOKING_ENTRY_STATES = [
  "pending",
  "scheduled",
  "finding_rider",
  "searching_rider",
  "reassigning",
  "active",
  "completed",
  "client_cancelled",
  "system_cancelled",
] as const;

export type BookingEntryState = (typeof BOOKING_ENTRY_STATES)[number];

export const BOOKING_LIFECYCLE_STATES = [
  "pending",
  "scheduled",
  "active",
  "completed",
  "client_cancelled",
  "system_cancelled",
] as const;

export type BookingLifecycleState = (typeof BOOKING_LIFECYCLE_STATES)[number];

/** A state off the wire, or null — an unknown value must cost a chip, not mislabel it. */
export function parseBookingEntryState(value: unknown): BookingEntryState | null {
  return typeof value === "string" && (BOOKING_ENTRY_STATES as readonly string[]).includes(value)
    ? (value as BookingEntryState)
    : null;
}

/** The booking-level counterpart of `parseBookingEntryState`. */
export function parseBookingLifecycleState(value: unknown): BookingLifecycleState | null {
  return typeof value === "string" &&
    (BOOKING_LIFECYCLE_STATES as readonly string[]).includes(value)
    ? (value as BookingLifecycleState)
    : null;
}

/**
 * Whether an entry is waiting on the system rather than moving — the three
 * states the spec says continue automatically (SCHED-010/011) and a screen
 * should keep polling for, because Searching Rider begins by the clock alone.
 */
export function isEntryRecovering(state: BookingEntryState): boolean {
  return state === "finding_rider" || state === "searching_rider" || state === "reassigning";
}

/**
 * Is a booking sitting at `awaiting_rider` still Reserved, rather than
 * actually having a rider arranged for it? True only before its own scheduled
 * start — the same boundary `v_booking_entry_state_base` (0227) draws between
 * its `scheduled` and `finding_rider` entry states, simplified for a plain
 * `BookingStatus` surface that does not carry `booking_kind`.
 *
 * That simplification is safe: an ASAP booking's `scheduled_at` is
 * effectively "now" at creation, so this never fires for one in practice —
 * there is no window between booking and `now()` for it to be true in.
 *
 * Leon's call, 2026-09-23: a client or therapist matched well ahead of a
 * Reserve booking's time should read "Reserved", not "Finding rider" /
 * "Arranging your rider" — that copy is only true once the clock reaches the
 * booking's own start, which is when the system actually begins searching.
 * Not yet in product-spec — flag for the vault (never-invent-policy.md).
 */
export function isReservedAwaitingWindow(
  status: string,
  scheduledAtIso: string,
  nowMs: number = Date.now(),
): boolean {
  if (status !== "awaiting_rider") return false;
  const scheduledMs = Date.parse(scheduledAtIso);
  return Number.isFinite(scheduledMs) && nowMs < scheduledMs;
}
